import classNames from "classnames";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsTrash, BsUpload } from "react-icons/bs";
import { useMediaQuery } from "react-responsive";
import { NAME_SPACES } from "shared/locales/constants";
import { bytesToMB } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import Head from "../Components/Head";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import validation from "./validation";

const View = ({ initialValues, onSubmit, locked, translate, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const [open, setOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        })
      ),
    });

  const onFileRemove = async ({ uid: id }) => {
    if (id.indexOf("AUTO") !== -1) return;
  };

  return (
    <Layout>
      <Head description={translate.DESCRIPTION} title={translate.TITLE} onBack={onBack} />

      <div className="lmf__form grid--1 gap-24">
        <div className="justification__section--child">
          <Input
            type="file"
            variant="uploader"
            listType="file"
            onUpload={(fileList) => {
              setFieldValue("contributionPaymentAgreement", fileList);
            }}
            validTypes={translate.DOCUMENTS.ALLOWED_FILES_TYPE}
            onFileRemove={onFileRemove}
            fileList={get(values, "contributionPaymentAgreement", [])}
          >
            <div className="upload">
              <div className="label">
                <span>
                  {translate.DOCUMENTS.TYPES.CONTRIBUTION_PAYMENT_AGREEMENT}
                  <small className="required">{translate.REQUIRED}</small>
                </span>
                {get(values, "contributionPaymentAgreement", []).map(
                  ({ name, size }, index) => (
                    <small key={`contributionPaymentAgreement-${index}`}>
                      {name} - {bytesToMB(size)}MB
                    </small>
                  )
                )}
              </div>
              <Button
                type={
                  get(values, "contributionPaymentAgreement", []).length
                    ? "danger-outline"
                    : "white"
                }
                size="middle"
                onClick={(e) => {
                  if (get(values, "contributionPaymentAgreement", []).length) {
                    e.stopPropagation();
                    setFieldValue("contributionPaymentAgreement", []);
                  }
                }}
              >
                {get(values, "contributionPaymentAgreement", []).length ? (
                  <BsTrash size={20} />
                ) : (
                  <BsUpload size={20} />
                )}
              </Button>
            </div>
          </Input>
          {get(formProps.errors, "contributionPaymentAgreement") && (
            <span className="error-message">
              {get(formProps.errors, "contributionPaymentAgreement")}
            </span>
          )}
        </div>
        <div className="justification__section--child">
          <Input
            type="file"
            variant="uploader"
            listType="picture"
            onUpload={(fileList) => {
              setFieldValue("payerCin", fileList);
            }}
            validTypes={translate.DOCUMENTS.ALLOWED_FILES_TYPE}
            onFileRemove={onFileRemove}
            fileList={get(values, "payerCin", [])}
          >
            <div className="upload">
              <div className="label">
                <span>
                  {translate.DOCUMENTS.TYPES.PAYER_CIN}
                  <small className="required">{translate.REQUIRED}</small>
                </span>
                {get(values, "payerCin", []).map(({ name, size }, index) => (
                  <small key={`payerCin-${index}`}>
                    {name} - {bytesToMB(size)}MB
                  </small>
                ))}
              </div>
              <Button
                type={
                  get(values, "payerCin", []).length
                    ? "danger-outline"
                    : "white"
                }
                size="middle"
                onClick={(e) => {
                  if (get(values, "payerCin", []).length) {
                    e.stopPropagation();
                    setFieldValue("payerCin", []);
                  }
                }}
              >
                {get(values, "payerCin", []).length ? (
                  <BsTrash size={20} />
                ) : (
                  <BsUpload size={20} />
                )}
              </Button>
            </div>
          </Input>
          {get(formProps.errors, "payerCin") && (
            <span className="error-message">
              {get(formProps.errors, "payerCin")}
            </span>
          )}
        </div>
        <div className="justification__section--child">
          <Input
            type="file"
            variant="uploader"
            listType="picture"
            onUpload={(fileList) => {
              setFieldValue("payerRib", fileList);
            }}
            validTypes={translate.DOCUMENTS.ALLOWED_FILES_TYPE}
            onFileRemove={onFileRemove}
            fileList={get(values, "payerRib", [])}
          >
            <div className="upload">
              <div className="label">
                <span>
                  {translate.DOCUMENTS.TYPES.PAYER_RIB}
                  <small className="required">{translate.REQUIRED}</small>
                </span>
                {get(values, "payerRib", []).map(({ name, size }, index) => (
                  <small key={`payerRib-${index}`}>
                    {name} - {bytesToMB(size)}MB
                  </small>
                ))}
              </div>
              <Button
                type={
                  get(values, "payerRib", []).length
                    ? "danger-outline"
                    : "white"
                }
                size="middle"
                onClick={(e) => {
                  if (get(values, "payerRib", []).length) {
                    e.stopPropagation();
                    setFieldValue("payerRib", []);
                  }
                }}
              >
                {get(values, "payerRib", []).length ? (
                  <BsTrash size={20} />
                ) : (
                  <BsUpload size={20} />
                )}
              </Button>
            </div>
          </Input>
          {get(formProps.errors, "payerRib") && (
            <span className="error-message">
              {get(formProps.errors, "payerRib")}
            </span>
          )}
        </div>
      </div>

      <ActionButtons
        buttonText={translate.NEXT}
        onNext={handleSubmit}
        disabled={!isEmpty(formProps.errors)}
        {...(isMobile && { onBack })}
        className={classNames({
          "w-100": isMobile,
        })}
      />
    </Layout>
  );
};

export default View;
